import React, { useState, useEffect } from 'react';
import { CommentWithContentContext } from 'shared/types/contentTypes';
import CommentCard from '../CommentCard/CommentCard';
import useCommentReplies from '../../../hooks/useCommentReplies';
import NewCommentForm from '../NewCommentForm';
import ContributeButton from '../../ContributeButton/ContributeButton';
import { Plus, Minus, MessageCircle } from 'lucide-react';

interface ReplyThreadProps {
  comment: CommentWithContentContext;
  contentId: string;
  initialReplies?: CommentWithContentContext[];
  onReply: (commentId: string, contentId: string, text: string, mentions?: import('shared/types/contentTypes').MentionMetadata[]) => Promise<void>;
  onLike: (commentId: string, isLiked: boolean, contentId: string) => Promise<void>;
  onEdit?: (commentId: string, text: string, contentId: string) => Promise<void>;
  onDelete?: (commentId: string, contentId: string) => Promise<void>;
  onCancelAddingReply?: () => void;
  currentUserId?: string;
  isExpanded: boolean;
  isAddingReply?: boolean;
  onToggleExpand: () => void;
  showContentContext?: boolean;
  /** Use 'response' mode for gestures (Respond/Add a response) vs 'contribution' for other content */
  responseMode?: boolean;
  /** Always show author attribution (name under comment, "You" for own comments) */
  showAuthorAttribution?: boolean;
}

const ReplyThread: React.FC<ReplyThreadProps> = ({
  comment,
  contentId,
  initialReplies = [],
  onReply,
  onLike,
  onEdit,
  onDelete,
  onCancelAddingReply,
  currentUserId,
  isExpanded,
  isAddingReply = false,
  onToggleExpand,
  showContentContext,
  responseMode = false,
  showAuthorAttribution = false
}) => {
  // State to track if we're using local replies or fetched replies
  const [useInitialReplies, setUseInitialReplies] = useState(true);
  const [isAddingPerspective, setIsAddingPerspective] = useState(false);
  const commentId = comment.commentId;

  // Initialize the hook for comment replies
  const {
    replies,
    isLoading,
    error,
    hasMoreReplies,
    loadMoreReplies,
    refreshReplies
  } = useCommentReplies({
    contentId,
    commentId,
    limit: 20
  });

  // Always fetch replies even if collapsed to know the count
  useEffect(() => {
    if (initialReplies.length === 0) {
      // No initial replies, switch to fetched replies
      setUseInitialReplies(false);
    } else {
      // Has initial replies, but also refresh from server
      refreshReplies();
    }
  }, [initialReplies.length, refreshReplies]);

  // Use initial replies if available and not explicitly fetching
  // Always sort chronologically (oldest first) for threaded conversation readability
  const displayReplies = (useInitialReplies ? initialReplies : replies)
    .slice()
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  const hasReplies = useInitialReplies ? initialReplies.length > 0 : replies.length > 0;
  const replyCount = displayReplies.length;
  
  // Show thread if there are replies OR if we're adding a reply
  const shouldShowThread = hasReplies || isAddingReply;

  // Handler for loading more replies
  const handleLoadMoreReplies = () => {
    setUseInitialReplies(false); // Switch to fetched replies
    loadMoreReplies();
  };

  // Handle submitting a new perspective
  const handleAddPerspective = async (text: string, mentions?: import('shared/types/contentTypes').MentionMetadata[]) => {
    await onReply(commentId, contentId, text, mentions);
    setIsAddingPerspective(false);
    refreshReplies();
  };

  // Handle submitting a new perspective (from CommentFeed)
  const handleSubmitNewReply = async (text: string, mentions?: import('shared/types/contentTypes').MentionMetadata[]) => {
    await onReply(commentId, contentId, text, mentions);
    refreshReplies();
    if (onCancelAddingReply) {
      onCancelAddingReply();
    }
  };

  // Handle clicking the toggle button or reply count bubble
  const handleToggleExpand = () => {
    onToggleExpand();
  };

  // Show loading indicator when expanding with no initial replies
  if (isLoading && displayReplies.length === 0) {
    return (
      <div className="mt-3 pl-4">
        <div className="animate-pulse space-y-3">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex-1 space-y-1">
              <div className="h-2 bg-gray-200 rounded w-3/4"></div>
              <div className="h-2 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !useInitialReplies) {
    return (
      <div className="pl-4 mt-3 text-red-500 text-sm">
        Failed to load perspectives. <button onClick={refreshReplies} className="underline">Try again</button>
      </div>
    );
  }

  // If no replies and not adding a reply, don't render anything
  if (!shouldShowThread) {
    return null;
  }

  return (
    <div className="">
      {/* Plus/Minus icon with toggle functionality */}
      {shouldShowThread && (
        <div className="ml-4 relative">
          <div
            className="absolute -top-2 left-1.5 w-3 h-3 rounded-full bg-[#faf9f7] border border-coral-purple/30 flex items-center justify-center z-10 cursor-pointer"
            aria-label={isExpanded ? "Collapse perspectives" : "Expand perspectives"}
            onClick={handleToggleExpand}
          >
            {isExpanded ?
              <Minus size={12} className="text-coral-purple/60" /> :
              <Plus size={12} className="text-coral-purple/60" />
            }
          </div>
        </div>
      )}
      
      {/* Display either the collapsed view or the expanded view */}
      {shouldShowThread && !isExpanded ? (
        /* Collapsed view - Show only a speech bubble with reply count but keep connector line that curves */
        <div className="space-y-0 relative ml-4" style={{ paddingLeft: '12px' }}>
          {/* Curved connector line styling similar to nested comments */}
          <div className="nested-comment">
            <style>{`
              .nested-comment {
                position: relative;
              }
              
              .nested-comment::before {
                content: '';
                position: absolute;
                top: 2px;
                left: 0px;
                width: 16px; 
                height: 12px;
                border-bottom: 1px solid rgb(141 134 201 / 0.3);
                border-left: 1px solid rgb(141 134 201 / 0.3);
                border-bottom-left-radius: 6px;
              }
            `}</style>
            {/* Speech bubble with reply count - positioned to match soundwave location */}
            <div className="flex items-start pt-0 ml-6">
              <div className="flex-shrink-0 pt-1 mr-2 min-w-[30px]">
                <button 
                  onClick={handleToggleExpand}
                  className="flex items-center gap-1 text-gray-400/60 hover:text-gray-600 text-sm transition-colors"
                  aria-label="Show perspectives"
                >
                  <MessageCircle size={16} />
                  <span>{replyCount} {replyCount === 1 ? 'contribution' : 'more contributions'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Expanded view - Show all replies */
        <>
          <div className={`space-y-0 relative ml-4 comment-thread ${displayReplies.length === 0 && isAddingReply ? 'only-form' : ''} ${displayReplies.length === 0 && currentUserId && !isAddingReply && !isAddingPerspective ? 'has-add-button' : ''} ${displayReplies.length > 0 && currentUserId && !isAddingReply && !isAddingPerspective ? 'has-replies-and-button' : ''} ${isAddingPerspective ? 'has-form-showing' : ''}`} style={{ paddingLeft: '12px' }}>
            {/* We'll use CSS for connector lines that stop at the last comment */}
            <style>{`
              .comment-thread {
                position: relative;
                margin-top: 8px;
              }

              .comment-thread::before {
                content: '';
                position: absolute;
                top: 0px;
                bottom: 5px; /* Stop before the bottom of the last comment */
                left: 12px;
                width: 1px;
                background-color: #8D86C9;
              }

              /* When there's only the form and no replies, stop the line just after the curve */
              .comment-thread.only-form::before {
                bottom: calc(100% - 10px);
              }

              /* When there's an add button at the bottom with no replies, stop the trunk at the button curve */
              .comment-thread.has-add-button::before {
                bottom: calc(100% - 50px);
              }

              /* When there are replies and a contribute button, stop after the last reply */
              .comment-thread.has-replies-and-button::before {
                bottom: 35px;
              }

              /* When the form is showing (after clicking Add a response), extend trunk to the form's curve */
              .comment-thread.has-form-showing::before {
                bottom: 5px;
              }
            `}</style>
            
            {/* New reply form as first item if adding a reply */}
            {isAddingReply && (
              <div className="pb-3 relative nested-comment">
                <style>{`
                  .nested-comment {
                    position: relative;
                  }

                  .nested-comment::before {
                    content: '';
                    position: absolute;
                    top: 2px;
                    left: 0px;
                    width: 16px;
                    height: 12px;
                    border-bottom: 1px solid #8D86C9;
                    border-left: 1px solid #8D86C9;
                    border-bottom-left-radius: 6px;
                  }
                `}</style>
                <div className="flex items-start">
                  <div className="flex-1">
                    <NewCommentForm
                      onSubmit={handleSubmitNewReply}
                      onCancel={onCancelAddingReply}
                      placeholder="Add your perspective..."
                      buttonText="Add"
                      autoFocus={true}
                    />
                  </div>
                </div>
              </div>
            )}
            
            {displayReplies.map((reply, index) => (
              <CommentCard
                key={reply.commentId}
                comment={reply}
                onLike={(commentId, isLiked) => onLike(commentId, isLiked, contentId)}
                onEdit={onEdit ? (commentId, text) => onEdit(commentId, text, contentId) : undefined}
                onDelete={onDelete ? (commentId) => onDelete(commentId, contentId) : undefined}
                currentUserId={currentUserId}
                nestingLevel={1}
                showReplies={false}
                isLastInThread={index === displayReplies.length - 1 && !isAddingPerspective && !currentUserId}
                showContentContext={showContentContext}
                showAuthorAttribution={showAuthorAttribution}
              />
            ))}
            
            {/* Show Add button at the bottom of perspectives - only in expanded view and not when already adding */}
            {currentUserId && !isAddingReply && (
              <div className="pb-3 relative nested-comment">
                <style>{`
                  .nested-comment {
                    position: relative;
                  }

                  .nested-comment::before {
                    content: '';
                    position: absolute;
                    top: 2px;
                    left: 0px;
                    width: 16px;
                    height: 12px;
                    border-bottom: 1px solid #8D86C9;
                    border-left: 1px solid #8D86C9;
                    border-bottom-left-radius: 6px;
                  }
                `}</style>
                <div className="flex items-start ml-4">
                  <div className="flex-1">
                    {isAddingPerspective ? (
                      <NewCommentForm
                        onSubmit={handleAddPerspective}
                        onCancel={() => setIsAddingPerspective(false)}
                        placeholder="Add your perspective..."
                        buttonText="Add"
                        autoFocus={true}
                      />
                    ) : (
                      <ContributeButton
                        onClick={() => setIsAddingPerspective(true)}
                        textMode={responseMode ? 'add-response' : 'add-response'}
                        size="secondary"
                        className="py-1"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Load more button - only visible in expanded view if there are more replies */}
          {!useInitialReplies && hasMoreReplies && (
            <button
              onClick={handleLoadMoreReplies}
              className="mt-4 ml-4 px-3 py-1 text-xs text-gray-600 font-medium"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Show more perspectives'}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default ReplyThread;